import React from 'react';
import { motion } from 'framer-motion';
import {
  Maximize2,
  Gauge,
  Anchor,
  SlidersHorizontal,
} from 'lucide-react';
import SectionHead from './SectionHead';
import BargeConfigurator from './BargeConfigurator';
import { OPTIONS } from '../data/bargeOptions';
import { EASE, Reveal } from './reveal';
import { track } from '../lib/track';

const HERO_SPECS = [
  { icon: Maximize2, label: 'Size', value: '8\u2032\u00d724\u2032 / 10\u2032\u00d730\u2032', sub: 'Two Hull Sizes' },
  { icon: Gauge, label: 'Power', value: 'Single / Twin Suzuki 115', sub: 'Choose Your Configuration' },
  { icon: Anchor, label: 'Payload', value: '15,000 lbs / 18,000 lbs +', sub: 'Rated Capacity' },
  { icon: SlidersHorizontal, label: 'Built', value: 'Highly Customizable Build', sub: 'Pick Your Exact Specifications' },
];

export default function OptionsSpecs() {
  return (
    <section id="specs" className="relative py-16 sm:py-20 lg:py-32 bg-black overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
      <div className="absolute -top-20 right-0 w-[320px] sm:w-[480px] h-[320px] sm:h-[480px] rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        <SectionHead
          eyebrow="Customize Your HDPE Barge"
          titleLines={[<>Options <span key="amp" className="text-cyan-300">&</span> Specs</>]}
          body="The Absolute HDPE Barge is built for maneuverability, functionality, and it's highly customizable to fit your business' specific needs. Start with the core platform, then configure the rig to your exact specifications."
        />

        <div className="mt-10 sm:mt-12 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {HERO_SPECS.map((s, idx) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.label}
                className="panel rounded-xl p-5 sm:p-6"
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, ease: EASE, delay: idx * 0.07 }}
              >
                <div className="flex items-center justify-between">
                  <Icon className="w-5 h-5 text-cyan-300" />
                  <span className="font-tech text-[10px] tracking-[0.24em] text-zinc-500 uppercase">
                    {s.label}
                  </span>
                </div>
                <div className="mt-4 sm:mt-5 font-display text-white text-base sm:text-lg font-semibold tracking-wide leading-[1.2]">
                  {s.value}
                </div>
                <div className="mt-1 text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-zinc-500">
                  {s.sub}
                </div>
              </motion.div>
            );
          })}
        </div>

        <Reveal className="mt-14 sm:mt-20" delay={0.15}>
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-8 bg-cyan-400/70" />
            <span className="font-tech text-cyan-300 text-[11px] uppercase tracking-[0.32em]">
              Available Options
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {OPTIONS.map((o, idx) => {
              const Icon = o.icon;
              return (
                <motion.div
                  key={o.label}
                  className="panel rounded-md px-4 py-3.5 flex items-center gap-3"
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.6, ease: EASE, delay: (idx % 6) * 0.05 }}
                >
                  <Icon className="w-4 h-4 text-cyan-300 shrink-0" />
                  <span className="text-zinc-200 text-sm">{o.label}</span>
                </motion.div>
              );
            })}
          </div>
        </Reveal>

        <Reveal className="mt-12 sm:mt-16" delay={0.25}>
          <div className="panel rounded-xl p-6 sm:p-7 md:p-9 flex flex-col md:flex-row items-start md:items-center justify-between gap-5 md:gap-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="h-px w-8 sm:w-10 bg-cyan-400" />
                <span className="font-tech text-cyan-300 text-[11px] uppercase tracking-[0.32em]">
                  Configure Your Barge
                </span>
              </div>
              <h3 className="font-display text-white text-lg sm:text-xl font-semibold tracking-wide">
                Let&apos;s build your perfect barge.
              </h3>
              <p className="mt-2 text-zinc-400 text-sm md:text-base max-w-2xl">
                Pick exactly what you want your new barge to have and we&apos;ll automatically include it in your quote request.
              </p>
            </div>
            <a
              href="/build"
              onClick={(e) => {
                // Only intercept plain left-clicks; let cmd/ctrl/middle-click
                // fall through to a full navigation to /build so the URL is
                // real and shareable.
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                e.preventDefault();
                track('buy_click', { location: 'specs' });
                if (window.location.pathname !== '/build') {
                  window.history.pushState({}, '', '/build');
                }
                window.dispatchEvent(new Event('open-barge-configurator'));
              }}
              data-testid="open-configurator-btn"
              className="btn-teal px-5 sm:px-6 py-3 rounded-md text-xs sm:text-sm uppercase tracking-[0.16em] sm:tracking-[0.18em] whitespace-nowrap"
            >
              Build My Barge
            </a>
          </div>
        </Reveal>
      </div>

      <BargeConfigurator />
    </section>
  );
}
